/**
 * `useCurrentPrincipal()` — surfaces the authenticated user / workspace
 * tuple from the auth + workspace providers in one shot.
 *
 * Per NFR-Design § 3. The dashboard's principal is ALWAYS `kind: "user"`
 * (cookie-only auth); the `kind: "apikey"` branch is dead code in the
 * dashboard but kept so the hook can be reused elsewhere.
 */

import { useAuth, useWorkspace } from "@cometchat-ai/auth/client";

export interface UserPrincipal {
  kind: "user";
  userId: string;
  workspaceId: string;
  /** Namespaced role tokens (`ws:admin`, `ws:custom:<id>`, `org:*`, …). */
  roles: ReadonlyArray<string>;
}

export interface ApiKeyPrincipal {
  kind: "apikey";
  workspaceId: string;
}

export type Principal = UserPrincipal | ApiKeyPrincipal;

/**
 * @throws if called outside `<AuthProvider>` + `<WorkspaceProvider>`, OR
 *         when the user isn't authenticated for the current workspace.
 */
export function useCurrentPrincipal(): UserPrincipal {
  const auth = useAuth();
  const { workspace } = useWorkspace();
  if (auth.status !== "authenticated" || !auth.user) {
    throw new Error("useCurrentPrincipal called without an authenticated session");
  }
  return {
    kind: "user",
    userId: auth.user.id,
    workspaceId: workspace.id,
    roles: auth.roles,
  };
}
